/**
 * Zustand Auth Store
 *
 * Client-side authentication state management with Zustand.
 * Manages user profile, roles, permissions, and auth status.
 *
 * SECURITY: NO persistence (no localStorage, no sessionStorage).
 * Authentication uses a single opaque session_id HTTP-only cookie
 * managed by the backend. User data is held in memory only — on page
 * refresh, the AuthContext calls /auth/session to rehydrate from the cookie.
 *
 * @module AuthStore
 */

import { create } from 'zustand';

/**
 * User identity (minimal — from backend response, NOT from Supabase client)
 */
export interface User {
  id: string;
  email: string;
}

/**
 * User profile from database
 */
export interface UserProfile {
  id: string;
  auth_user_id: string;
  primary_email: string;
  first_name?: string;
  last_name?: string;
  display_first_name?: string;
  display_last_name?: string;
  phone?: string;
  avatar_url?: string;
  organization_id?: string;
  session_valid_after?: string;
  // Standard License & Data-Usage Agreement acceptance. Null/absent means the
  // user has never accepted; an older value means the agreement was updated
  // since they accepted. Either way the backend sends termsAcceptanceRequired=true.
  terms_version_accepted?: string | null;
  terms_accepted_at?: string | null;
  // Soft-deactivation flag set by an admin via the user-details page. When
  // false, the auth flow logs the user out and routes them to /account-deactivated.
  is_active?: boolean;
  deactivated_at?: string | null;
  deactivated_by_user_id?: string | null;
  deactivation_reason?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Role type from database
 */
export interface Role {
  id: string;
  role_name: string;  // IMPORTANT: Uses 'role_name', not 'name'
  role_type: string;  // 'system' or 'org'
  description?: string;
  is_default: boolean;
  // Backend flags (from UserRole response)
  isAdmin?: boolean;
  allOrgAdmin?: boolean;
}

/**
 * Permission type from database
 */
export interface Permission {
  id: string;
  perm_name: string;  // e.g., 'dashboard:view', 'users:edit'
  description?: string;
}

/**
 * Impersonation banner context — populated when an admin is currently
 * impersonating this user. Returned by /auth/session, /auth/me, /auth/refresh.
 *
 * `null` (or absent) = not impersonating. The banner reads `isImpersonating`
 * to decide whether to render, and uses the display name fields to surface
 * "Return to <admin>".
 */
export interface ImpersonatorContext {
  isImpersonating: boolean;
  impersonatorUserId: string;
  impersonatorEmail: string | null;
  impersonatorDisplayName: string | null;
}

/**
 * Authentication state — in-memory only, no persistence
 */
interface AuthState {
  // User data
  user: User | null;
  profile: UserProfile | null;
  role: Role | null;
  permissions: Permission[];

  // Impersonation context — null when the current session is the user's own.
  // Populated when an admin is impersonating this user; drives the global
  // ImpersonationBanner.
  impersonator: ImpersonatorContext | null;

  // True when the user has not yet accepted the current Standard License &
  // Data-Usage Agreement (never accepted, or accepted an older version).
  // Drives the global blocking TermsAcceptanceGate.
  termsAcceptanceRequired: boolean;

  // Auth status
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Authentication actions
 */
interface AuthActions {
  // Set auth data (login/signup/session restore)
  setAuthData: (data: {
    user: User;
    profile: UserProfile;
    role: Role;
    permissions: Permission[];
    impersonator?: ImpersonatorContext | null;
    termsAcceptanceRequired?: boolean;
  }) => void;

  // Update specific parts
  updateProfile: (profile: UserProfile) => void;
  updateRole: (role: Role, permissions: Permission[]) => void;
  updatePermissions: (permissions: string[]) => void;
  // Called after the user accepts the current agreement via the gate.
  setTermsAccepted: () => void;

  // Session management
  logout: () => void;
  clearAuth: () => void;

  // Permission checking
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;

  // Role checking
  hasRole: (roleName: string) => boolean;
  isAdmin: () => boolean;
  isAllOrgAdmin: () => boolean;

  // Loading state
  setLoading: (loading: boolean) => void;
}

/**
 * Complete auth store type
 */
type AuthStore = AuthState & AuthActions;

/**
 * Initial state
 */
const initialState: AuthState = {
  user: null,
  profile: null,
  role: null,
  permissions: [],
  impersonator: null,
  termsAcceptanceRequired: false,
  isAuthenticated: false,
  isLoading: true, // Start loading — AuthContext will resolve via /auth/session
};

// State for an explicitly cleared session (logout, 401 stomp).
// Same as initialState but with isLoading=false. The `isLoading: true` default
// only makes sense at app boot when AuthContext is racing to resolve
// /auth/session — after logout there is nothing to wait for, and leaving
// isLoading=true strands the app on the App.tsx global spinner if a stale
// in-flight request returns 401 post-logout (the http-client 401 handler
// calls clearAuth on every 401, including orphaned responses from queries
// the user already navigated away from).
const clearedState: AuthState = {
  ...initialState,
  isLoading: false,
};

/**
 * Zustand auth store — in-memory only, no persistence.
 *
 * On page refresh, AuthContext calls /auth/session to rehydrate.
 * The HTTP-only session_id cookie (persistent, 7-day maxAge) handles
 * "remember me" — the backend validates the session and returns user data.
 */
export const useAuthStore = create<AuthStore>()(
  (set, get) => ({
    ...initialState,

    /**
     * Set complete auth data (login/signup/session restore)
     */
    setAuthData: (data) => {
      set({
        user: data.user,
        profile: data.profile,
        role: data.role,
        permissions: data.permissions,
        // Default to null so a non-impersonation login clears any prior
        // banner state (e.g. after stop-impersonate the response carries
        // no impersonator block).
        impersonator: data.impersonator ?? null,
        termsAcceptanceRequired: data.termsAcceptanceRequired ?? false,
        isAuthenticated: true,
        isLoading: false,
      });
    },

    /**
     * Update user profile
     */
    updateProfile: (profile) => {
      set({ profile });
    },

    /**
     * Clear the terms-acceptance requirement after the user accepts via the gate.
     */
    setTermsAccepted: () => {
      set({ termsAcceptanceRequired: false });
    },

    /**
     * Update user role and permissions
     */
    updateRole: (role, permissions) => {
      set({ role, permissions });
    },

    /**
     * Update just permissions (from API response with string array)
     */
    updatePermissions: (permissionNames) => {
      const permissions = permissionNames.map(name => ({
        id: name,
        perm_name: name,
        description: undefined
      }));
      set({ permissions });
    },

    /**
     * Logout - clear all auth data
     */
    logout: () => {
      set(clearedState);
    },

    /**
     * Clear auth - used by http-client on 401/session revocation
     */
    clearAuth: () => {
      set(clearedState);
    },

    /**
     * Check if user has specific permission
     */
    hasPermission: (permission) => {
      const { permissions } = get();
      return permissions.some(p => p.perm_name === permission);
    },

    /**
     * Check if user has any of the specified permissions
     */
    hasAnyPermission: (permissionList) => {
      const { permissions } = get();
      return permissionList.some(perm =>
        permissions.some(p => p.perm_name === perm)
      );
    },

    /**
     * Check if user has all specified permissions
     */
    hasAllPermissions: (permissionList) => {
      const { permissions } = get();
      return permissionList.every(perm =>
        permissions.some(p => p.perm_name === perm)
      );
    },

    /**
     * Check if user has specific role.
     */
    hasRole: (roleName) => {
      const { role } = get();
      return role?.role_name === roleName;
    },

    /**
     * Check if user is admin (any org, including org-specific)
     */
    isAdmin: () => {
      const { role } = get();
      return role?.isAdmin === true || role?.role_name === 'admin';
    },

    /**
     * Check if user is ALL-org admin (system-wide, unrestricted)
     */
    isAllOrgAdmin: () => {
      const { role } = get();
      return role?.allOrgAdmin === true;
    },

    /**
     * Set loading state
     */
    setLoading: (loading) => {
      set({ isLoading: loading });
    },
  })
);

/**
 * Selector hooks for optimized re-renders
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useProfile = () => useAuthStore((state) => state.profile);
export const useRole = () => useAuthStore((state) => state.role);
export const usePermissions = () => useAuthStore((state) => state.permissions);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);
export const useImpersonator = () => useAuthStore((state) => state.impersonator);
export const useTermsAcceptanceRequired = () => useAuthStore((state) => state.termsAcceptanceRequired);
